import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Stack, StackProps } from '@components/stack';
import { VisuallyHidden } from '@components/visually-hidden';

import { useFormContext } from './form-provider';

export type FormActionsProps = StackProps & {
  portal?: string;
};

export const FormActions = memo<FormActionsProps>(
  ({ space = 4, orientation = 'horizontal', justify = 'flex-end', portal, children, ...rest }) => {
    const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

    useEffect(() => {
      if (!portal) return;
      const el = document.getElementById(portal);
      if (el) setPortalElement(el);
    }, [portal]);

    if (portal && !portalElement) return null;

    const content = (
      <Stack space={space} orientation={orientation} justify={justify} {...rest}>
        {children}
      </Stack>
    );

    if (!portalElement) return content;

    return (
      <>
        <HiddenFormSubmit />
        {createPortal(content, portalElement)}
      </>
    );
  }
);

FormActions.displayName = 'Form.Actions';

const HiddenFormSubmit = () => {
  const {
    store: { useIsSubmitDisabled },
  } = useFormContext();
  const isSubmitDisabled = useIsSubmitDisabled();

  return (
    <VisuallyHidden>
      <input type="submit" disabled={isSubmitDisabled} />
    </VisuallyHidden>
  );
};
